<script setup lang="ts">
import { ref, computed } from "vue"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import axios from "axios"
import { useI18n } from 'vue-i18n';
import { useToast } from "@/components/ui/toast"

import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuGroup,
} from '@/components/ui/dropdown-menu'

import { Pin, PinOff, Settings} from "lucide-vue-next";
import { supportsStickyColumns } from "@/utils/columnPresentation"


const { t } = useI18n();
const { toast } = useToast();

const columnSearch = ref("")
const stickyRequestsInFlight = new Set<string>()

const filteredColumns = computed(() => {
  const q = columnSearch.value.toLowerCase().trim()
  const cols = props.columns.filter(c => c.key !== 'actions' && c.key !== 'buffer')
  if (!q) return cols
  return cols.filter(c => c.label?.toLowerCase().includes(q))
})

const props = withDefaults(defineProps<{
  columns: ColumnDef[]
  endpoint?: string
  enableAll?: boolean
  ownColumnKeys?: string[]
}>(), {
  enableAll: false,
  ownColumnKeys: () => []
})


const emit = defineEmits<{
  (e: "toggleColumn", change: "visibility" | "sticky", column?: { key: string; sticky: number }): void;
}>();

const canToggleSticky = computed(() => supportsStickyColumns(props.endpoint))

function isStickyColumn(col) {
  return Number(col.sticky || 0) > 0
}

function layoutColumns() {
  return props.columns.filter(c => c.key !== 'actions' && c.key !== 'buffer')
}

async function toggleStickyColumn(col) {
  if (!props.endpoint || !canToggleSticky.value) return

  const columnKey = String(col.key)
  if (stickyRequestsInFlight.has(columnKey)) return

  const previousSticky = Number(col.sticky || 0)
  const optimisticSticky = previousSticky > 0 ? 0 : 1
  stickyRequestsInFlight.add(columnKey)
  col.sticky = optimisticSticky
  emit("toggleColumn", "sticky", { key: columnKey, sticky: optimisticSticky });

  try {
    const response = await axios.post(`/${props.endpoint}/togglesticky`, {
      column: col.key
    });
    const savedSticky = Number(response.data?.sticky || 0)
    if (savedSticky !== optimisticSticky) {
      const currentColumn = props.columns.find(c => String(c.key) === columnKey) || col
      currentColumn.sticky = savedSticky
      emit("toggleColumn", "sticky", { key: columnKey, sticky: savedSticky });
    }
  } catch (error: any) {
    const currentColumn = props.columns.find(c => String(c.key) === columnKey) || col
    currentColumn.sticky = previousSticky
    emit("toggleColumn", "sticky", { key: columnKey, sticky: previousSticky });
    const message =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.response?.statusText ||
      `Failed to update ${col.label}`;
    toast({ title: "Error", description: message, variant: "destructive" });
  } finally {
    stickyRequestsInFlight.delete(columnKey)
  }
}

async function persistColumnToggle(col) {
  if (!props.endpoint) return
  try {
    await axios.post(`/${props.endpoint}/togglecol`, {
      column: col.key,
      show: col.show
    });
  } catch (error: any) {
    const message =
      error.response?.data?.message ||
      error.response?.statusText ||
      `Failed to update ${col.label}`;
    toast({ title: "Error", description: message, variant: "destructive" });
  }
}

async function persistColumnBatch(cols) {
  if (!props.endpoint || cols.length === 0) return
  try {
    await axios.post(`/${props.endpoint}/updatecols`, {
      columns: cols.map(c => ({
        key: c.key,
        show: c.show
      }))
    });
  } catch (error: any) {
    const message =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.response?.statusText ||
      "Failed to update columns";
    toast({ title: "Error", description: message, variant: "destructive" });
  }
}

async function toggleColumn(col, withEmit = true) {
  console.log("toggleColumn col=" + col.key)
  await persistColumnToggle(col)
  // Tables without `endpoint` (e.g. assignment matrix with only `:data`) still need this so
  // DataTable can persist column visibility via columnStore and refresh the grid.
  if (withEmit) {
    emit("toggleColumn", "visibility");
  }
}

async function selectAllColumns() {
  const changed = []
  for (const c of layoutColumns()) {
    if (!c.show) {
      c.show = true
      changed.push(c)
    }
  }
  await persistColumnBatch(changed)
  emit("toggleColumn", "visibility")
}

async function deselectAllColumns() {
  const changed = []
  for (const c of layoutColumns()) {
    if (c.show) {
      c.show = false
      changed.push(c)
    }
  }
  await persistColumnBatch(changed)
  emit("toggleColumn", "visibility")
}

async function selectOwnColumns() {
  const ownSet = new Set(props.ownColumnKeys)
  const changed = []
  for (const c of layoutColumns()) {
    const shouldShow = ownSet.has(c.key)
    if (c.show !== shouldShow) {
      c.show = shouldShow
      changed.push(c)
    }
  }
  await persistColumnBatch(changed)
  emit("toggleColumn", "visibility")
}
console.log("[ColumnSelect] ownColumnKeys=" + JSON.stringify(props.ownColumnKeys) + " columns=" + JSON.stringify(props.columns.map(c => c.key)))

</script>

<template>
<DropdownMenu>
  <DropdownMenuTrigger asChild>
    <Button variant="outline" size="sm" :title="t('view.columns_hs')">
      <Settings/> {{ t('view.columns') }}
    </Button>
  </DropdownMenuTrigger>

  <DropdownMenuContent class="w-60 max-h-80 z-[10001]" @closeAutoFocus.prevent>
    <DropdownMenuLabel>{{ t('view.columns_hs') }}</DropdownMenuLabel>

    <div class="px-2 pb-2">
      <Input
        v-model="columnSearch"
        :placeholder="t('view.filter_placeholder')"
        class="h-7 text-sm"
        @keydown.stop
      />
    </div>

    <DropdownMenuSeparator />

    <DropdownMenuItem v-if="props.enableAll"
      class="flex items-center gap-2 px-2 py-1 font-semibold text-blue-600 cursor-pointer"
      @click="selectAllColumns"
    >
      {{ t('view.all') }}
    </DropdownMenuItem>

    <DropdownMenuItem v-if="props.enableAll"
      class="flex items-center gap-2 px-2 py-1 font-semibold text-blue-600 cursor-pointer"
      @click="deselectAllColumns"
    >
      {{ t('view.none') }}
    </DropdownMenuItem>

    <DropdownMenuItem v-if="props.ownColumnKeys && props.ownColumnKeys.length > 0"
      class="flex items-center gap-2 px-2 py-1 font-semibold text-blue-600 cursor-pointer"
      @click="selectOwnColumns"
    >
      {{ t('view.own') }}
    </DropdownMenuItem>

    <DropdownMenuSeparator />

    <DropdownMenuGroup class="max-h-48 overflow-y-auto">
      <DropdownMenuItem
        v-for="c in filteredColumns"
        :key="c.key"
        class="flex items-center justify-between"
      >
        <label class="flex items-center gap-2 cursor-pointer min-w-0 flex-1">
          <input 
            :disabled="!c.togglefilter"
            type="checkbox" 
            v-model="c.show" 
            class="accent-blue-500"
            @change="toggleColumn(c)"
          />
          <span class="truncate"> {{ c.label }} </span>
        </label>
        <Button
          v-if="canToggleSticky"
          type="button"
          variant="ghost"
          size="icon"
          class="h-7 w-7 shrink-0"
          :title="isStickyColumn(c) ? 'Spalte lösen' : 'Spalte fixieren'"
          @click.stop.prevent="toggleStickyColumn(c)"
        >
          <PinOff v-if="isStickyColumn(c)" class="w-4 h-4 text-blue-600" />
          <Pin v-else class="w-4 h-4 text-gray-500" />
        </Button>
      </DropdownMenuItem>
    </DropdownMenuGroup>
  </DropdownMenuContent>
</DropdownMenu>
</template>
